import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, User, Users, Target, Newspaper, CalendarDays,
  LayoutGrid, Phone, GraduationCap, Settings, LogOut, Menu, ExternalLink, Mail, Database,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { resolveImg } from "@/lib/api";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "dashboard" },
  { to: "/admin/profil", label: "Kelola Profil", icon: User, testid: "profil" },
  { to: "/admin/guru", label: "Kelola Guru", icon: Users, testid: "guru" },
  { to: "/admin/visi-misi", label: "Kelola Visi & Misi", icon: Target, testid: "visi-misi" },
  { to: "/admin/berita", label: "Kelola Berita", icon: Newspaper, testid: "berita" },
  { to: "/admin/kegiatan", label: "Kelola Kegiatan", icon: CalendarDays, testid: "kegiatan" },
  { to: "/admin/galeri", label: "Kelola Galeri", icon: LayoutGrid, testid: "galeri" },
  { to: "/admin/kontak", label: "Pesan Masuk", icon: Mail, testid: "kontak" },
  { to: "/admin/pendaftaran", label: "Link Pendaftaran", icon: GraduationCap, testid: "pendaftaran" },
  { to: "/admin/pengaturan", label: "Pengaturan Website", icon: Settings, testid: "pengaturan" },
  { to: "/admin/backup", label: "Database & Backup", icon: Database, testid: "backup" },
];

const NavList = ({ onNavigate }) => (
  <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
    {items.map((it) => (
      <NavLink
        key={it.to}
        to={it.to}
        end={it.end}
        onClick={onNavigate}
        data-testid={`admin-nav-${it.testid}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${
            isActive ? "bg-emerald-600 text-white shadow-sm" : "text-slate-300 hover:bg-slate-800 hover:text-white"
          }`
        }
      >
        <it.icon className="h-[18px] w-[18px]" /> {it.label}
      </NavLink>
    ))}
  </nav>
);

const AdminLayout = () => {
  const { logout } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const Brand = () => (
    <div className="flex items-center gap-3 border-b border-slate-800 px-5 py-5">
      <img src={resolveImg(settings.logo_url)} alt="Logo" className="h-11 w-11 rounded-full bg-white object-contain p-0.5" />
      <div className="leading-tight">
        <p className="font-heading text-sm font-bold text-white">Admin Panel</p>
        <p className="text-[11px] text-emerald-400">MI Futuhiyyah</p>
      </div>
    </div>
  );

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-100">
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col bg-slate-900 md:flex lg:w-72">
        <Brand />
        <NavList />
        <div className="space-y-2 border-t border-slate-800 p-3">
          <Link to="/" target="_blank">
            <Button variant="ghost" className="w-full justify-start gap-2 text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="admin-view-site">
              <ExternalLink className="h-4 w-4" /> Lihat Website
            </Button>
          </Link>
          <Button variant="ghost" onClick={doLogout} className="w-full justify-start gap-2 text-red-300 hover:bg-red-950/50 hover:text-red-200" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> Logout
          </Button>
        </div>
      </aside>

      <header className="sticky top-0 z-50 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <p className="font-heading font-bold text-slate-800">Admin Panel</p>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="admin-mobile-menu"><Menu className="h-6 w-6" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-slate-800 bg-slate-900 p-0">
            <Brand />
            <NavList onNavigate={() => setOpen(false)} />
            <div className="border-t border-slate-800 p-3">
              <Button variant="ghost" onClick={doLogout} className="w-full justify-start gap-2 text-red-300 hover:bg-red-950/50">
                <LogOut className="h-4 w-4" /> Logout
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      <main className="md:ml-64 lg:ml-72">
        <div className="mx-auto max-w-5xl px-5 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
